import { request } from '@umijs/max';
import { saveTokens, saveUserInfo, clearTokens, getRefreshToken, updateAccessToken, getAccessToken } from './tokenManager';

const AUTH_BASE_URL = process.env.Auth_URL || '';

export async function preRegister(params: AuthAPI.PreRegisterParams): Promise<AuthAPI.Response<AuthAPI.PreRegisterData>> {
  return request(`${AUTH_BASE_URL}/api/auth/pre-register`, {
    method: 'POST',
    data: params,
    skipErrorHandler: true,
  });
}

export async function resendRegisterCode(params: AuthAPI.ResendCodeParams): Promise<AuthAPI.Response<AuthAPI.PreRegisterData>> {
  return request(`${AUTH_BASE_URL}/api/auth/pre-register/resend`, {
    method: 'POST',
    data: params,
    skipErrorHandler: true,
  });
}

export async function verifyRegister(params: AuthAPI.VerifyRegisterParams): Promise<AuthAPI.Response<AuthAPI.VerifyRegisterData>> {
  const response = await request<AuthAPI.Response<AuthAPI.VerifyRegisterData>>(`${AUTH_BASE_URL}/api/auth/register/verify`, {
    method: 'POST',
    data: params,
    skipErrorHandler: true,
  });

  if (response.success && response.data) {
    saveTokens(response.data.tokens);
    saveUserInfo(response.data.user);
  }

  return response;
}

export async function preLoginPhone(params: AuthAPI.PreLoginPhoneParams): Promise<AuthAPI.Response<AuthAPI.PreLoginPhoneData>> {
  return request(`${AUTH_BASE_URL}/api/auth/pre-login-phone`, {
    method: 'POST',
    data: params,
    skipErrorHandler: true,
  });
}

export async function resendPhoneCode(params: AuthAPI.ResendPhoneCodeParams): Promise<AuthAPI.Response<AuthAPI.PreLoginPhoneData>> {
  return request(`${AUTH_BASE_URL}/api/auth/pre-login-phone/resend`, {
    method: 'POST',
    data: params,
    skipErrorHandler: true,
  });
}

export async function verifyPhoneLogin(params: AuthAPI.VerifyPhoneLoginParams): Promise<AuthAPI.Response<AuthAPI.VerifyLoginData>> {
  const response = await request<AuthAPI.Response<AuthAPI.VerifyLoginData>>(`${AUTH_BASE_URL}/api/auth/login-phone/verify`, {
    method: 'POST',
    data: params,
    skipErrorHandler: true,
  });

  if (response.success && response.data) {
    saveTokens(response.data.tokens);
    saveUserInfo(response.data.user);
  }

  return response;
}

export async function preLogin(params: AuthAPI.PreLoginParams): Promise<AuthAPI.Response<AuthAPI.PreLoginData>> {
  return request(`${AUTH_BASE_URL}/api/auth/pre-login`, {
    method: 'POST',
    data: params,
    skipErrorHandler: true,
  });
}

export async function resendLoginCode(params: AuthAPI.ResendCodeParams): Promise<AuthAPI.Response<AuthAPI.PreLoginData>> {
  return request(`${AUTH_BASE_URL}/api/auth/pre-login/resend`, {
    method: 'POST',
    data: params,
    skipErrorHandler: true,
  });
}

export async function verifyLogin(params: AuthAPI.VerifyLoginParams): Promise<AuthAPI.Response<AuthAPI.VerifyLoginData>> {
  const response = await request<AuthAPI.Response<AuthAPI.VerifyLoginData>>(`${AUTH_BASE_URL}/api/auth/login/verify`, {
    method: 'POST',
    data: params,
    skipErrorHandler: true,
  });

  if (response.success && response.data) {
    saveTokens(response.data.tokens);
    saveUserInfo(response.data.user);
  }

  return response;
}

export async function googleLogin(params: AuthAPI.GoogleLoginParams): Promise<AuthAPI.Response<AuthAPI.GoogleLoginData>> {

  const response = await fetch(`${AUTH_BASE_URL}/api/auth/google-login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  const data = await response.json() as AuthAPI.Response<AuthAPI.GoogleLoginData>;

  if (data.success && data.data) {
    saveTokens(data.data.tokens);
    saveUserInfo(data.data.user);
  }

  return data;
}

export async function forgotPassword(params: AuthAPI.ForgotPasswordParams): Promise<AuthAPI.Response<{ email: string; expiresIn: number }>> {
  return request(`${AUTH_BASE_URL}/api/auth/forgot-password`, {
    method: 'POST',
    data: params,
    skipErrorHandler: true,
  });
}

export async function resendForgotPasswordCode(params: AuthAPI.ResendCodeParams): Promise<AuthAPI.Response<{ email: string; expiresIn: number }>> {
  return request(`${AUTH_BASE_URL}/api/auth/forgot-password/resend`, {
    method: 'POST',
    data: params,
    skipErrorHandler: true,
  });
}

export async function resetPassword(params: AuthAPI.ResetPasswordParams): Promise<AuthAPI.Response<{ message: string }>> {
  return request(`${AUTH_BASE_URL}/api/auth/reset-password`, {
    method: 'POST',
    data: params,
    skipErrorHandler: true,
  });
}

export async function logout(): Promise<AuthAPI.Response<void>> {
  const response = await request<AuthAPI.Response<void>>(`${AUTH_BASE_URL}/api/auth/logout`, {
    method: 'POST',
    skipErrorHandler: true,
  });

  clearTokens();

  return response;
}

export async function refreshToken(): Promise<AuthAPI.Response<AuthAPI.RefreshTokenData>> {
  const refreshTokenValue = getRefreshToken();

  if (!refreshTokenValue) {
    return {
      success: false,
      message: 'No refresh token available',
      error: { code: 'E093' },
    };
  }

  const response = await request<AuthAPI.Response<AuthAPI.RefreshTokenData>>(`${AUTH_BASE_URL}/api/auth/refresh-token`, {
    method: 'POST',
    data: { refreshToken: refreshTokenValue },
    skipErrorHandler: true,
  });

  if (response.success && response.data) {
    updateAccessToken(response.data.accessToken, response.data.accessTokenExpiry);
  }

  return response;
}

export async function getCurrentUser(): Promise<AuthAPI.Response<AuthAPI.ProfileData>> {
  return request(`${AUTH_BASE_URL}/api/auth/profile`, {
    method: 'GET',
    skipErrorHandler: true,
  });
}

export async function updateProfile(params: AuthAPI.UpdateProfileParams): Promise<AuthAPI.Response<{ user: AuthAPI.UserInfo }>> {
  const response = await request<AuthAPI.Response<{ user: AuthAPI.UserInfo }>>(`${AUTH_BASE_URL}/api/auth/profile`, {
    method: 'PUT',
    data: params,
    skipErrorHandler: true,
  });

  if (response.success && response.data) {
    saveUserInfo(response.data.user);
  }

  return response;
}

export async function changePassword(params: AuthAPI.ChangePasswordParams): Promise<AuthAPI.Response<AuthAPI.ChangePasswordData>> {
  return request(`${AUTH_BASE_URL}/api/auth/change-password`, {
    method: 'POST',
    data: params,
    skipErrorHandler: true,
  });
}

export async function getEmailStatus(): Promise<AuthAPI.Response<AuthAPI.EmailStatusData>> {
  return request(`${AUTH_BASE_URL}/api/email/status`, {
    method: 'GET',
    skipErrorHandler: true,
  });
}

export async function sendCurrentEmailCode(params: AuthAPI.SendEmailCodeParams): Promise<AuthAPI.Response<AuthAPI.SendEmailCodeData>> {
  return request(`${AUTH_BASE_URL}/api/email/send-verification-code`, {
    method: 'POST',
    data: params,
    skipErrorHandler: true,
  });
}

export async function sendNewEmailCode(params: AuthAPI.SendEmailCodeParams): Promise<AuthAPI.Response<AuthAPI.SendEmailCodeData>> {
  return request(`${AUTH_BASE_URL}/api/email/send-new-email-code`, {
    method: 'POST',
    data: params,
    skipErrorHandler: true,
  });
}

export async function verifyCurrentEmail(params: AuthAPI.VerifyCurrentEmailParams): Promise<AuthAPI.Response<AuthAPI.VerifyCurrentEmailData>> {
  return request(`${AUTH_BASE_URL}/api/email/change`, {
    method: 'POST',
    data: params,
    skipErrorHandler: true,
  });
}

export async function bindNewEmail(params: AuthAPI.BindNewEmailParams): Promise<AuthAPI.Response<AuthAPI.BindNewEmailData>> {
  const response = await request<AuthAPI.Response<AuthAPI.BindNewEmailData>>(`${AUTH_BASE_URL}/api/email/change`, {
    method: 'POST',
    data: params,
    skipErrorHandler: true,
  });

  if (response.success && response.data) {
    saveUserInfo(response.data.user);
  }

  return response;
}

export async function uploadFile(file: File): Promise<{ code: number; message: string; data: AuthAPI.UploadFileData }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('source_type', 'profile_avatar');
  formData.append('folder', 'avatars');

  const token = getAccessToken();
  const resp = await fetch(`${AUTH_BASE_URL}/api/workspace/imports/upload`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: formData,
  });
  const json = await resp.json().catch(() => ({}));
  const data = json?.data || {};
  const url = data.minio_url || data.url || '';
  if (!resp.ok || !json?.success || !url) {
    return {
      code: json?.code || resp.status || 500,
      message: json?.message || 'upload failed',
      data: {} as AuthAPI.UploadFileData,
    };
  }
  return {
    code: 200,
    message: json?.message || 'ok',
    data: {
      filename: data.name || file.name,
      originalName: file.name,
      size: data.bytes || file.size,
      mimetype: data.content_type || file.type,
      url,
    },
  };
}

export async function getMyApiKeys(): Promise<AuthAPI.Response<AuthAPI.ApiKeyListData>> {
  return request(`${AUTH_BASE_URL}/api/keys`, {
    method: 'GET',
    skipErrorHandler: true,
  });
}

export async function createApiKey(params: AuthAPI.CreateApiKeyParams): Promise<AuthAPI.Response<AuthAPI.CreateApiKeyData>> {
  return request(`${AUTH_BASE_URL}/api/keys`, {
    method: 'POST',
    data: params,
    skipErrorHandler: true,
  });
}

export async function revokeApiKey(id: string): Promise<AuthAPI.Response<void>> {
  return request(`${AUTH_BASE_URL}/api/keys/${id}`, {
    method: 'DELETE',
    skipErrorHandler: true,
  });
}

export async function getSessions(params?: AuthAPI.GetSessionsParams): Promise<AuthAPI.Response<AuthAPI.SessionListData>> {
  return request(`${AUTH_BASE_URL}/api/sessions`, {
    method: 'GET',
    params,
    skipErrorHandler: true,
  });
}

export async function getSessionCount(): Promise<AuthAPI.Response<AuthAPI.SessionCountData>> {
  return request(`${AUTH_BASE_URL}/api/sessions/count`, {
    method: 'GET',
    skipErrorHandler: true,
  });
}

export async function getLoginHistory(params?: AuthAPI.GetLoginHistoryParams): Promise<AuthAPI.Response<AuthAPI.LoginHistoryData>> {
  return request(`${AUTH_BASE_URL}/api/sessions/history`, {
    method: 'GET',
    params,
    skipErrorHandler: true,
  });
}

export async function revokeSession(id: string): Promise<AuthAPI.Response<void>> {
  return request(`${AUTH_BASE_URL}/api/sessions/${id}`, {
    method: 'DELETE',
    skipErrorHandler: true,
  });
}

export async function revokeOtherSessions(): Promise<AuthAPI.Response<AuthAPI.RevokeOthersData>> {
  return request(`${AUTH_BASE_URL}/api/sessions/revoke-others`, {
    method: 'POST',
    skipErrorHandler: true,
  });
}

export async function getAuthOperationLogs(
  params?: AuthAPI.GetAuthOperationLogsParams,
): Promise<AuthAPI.GetAuthOperationLogsResponse> {
  return request(`${AUTH_BASE_URL}/api/admin/operation-logs`, {
    method: 'GET',
    params,
  });
}

export async function getDailyStats(
  params?: AuthAPI.GetDailyStatsParams,
): Promise<AuthAPI.GetDailyStatsResponse> {
  return request(`${AUTH_BASE_URL}/api/admin/operation-logs/daily-stats`, {
    method: 'GET',
    params,
  });
}

export async function getDailyCommissionStats(
  params?: AuthAPI.GetDailyCommissionStatsParams,
): Promise<AuthAPI.GetDailyCommissionStatsResponse> {
  return request(`${AUTH_BASE_URL}/api/admin/operation-logs/daily-commission-stats`, {
    method: 'GET',
    params,
  });
}

export async function recalculateStats(
  params: AuthAPI.RecalculateStatsParams,
): Promise<AuthAPI.RecalculateStatsResponse> {
  return request(`${AUTH_BASE_URL}/api/admin/operation-logs/recalculate-stats`, {
    method: 'POST',
    data: params,
  });
}

export async function getAdminUsers(
  params?: AuthAPI.AdminUserListParams,
): Promise<AuthAPI.Response<AuthAPI.AdminUserListData>> {
  return request(`${AUTH_BASE_URL}/api/admin/users`, {
    method: 'GET',
    params,
    skipErrorHandler: true,
  });
}

export async function getAdminUsersByRole(
  role: string,
): Promise<AuthAPI.Response<AuthAPI.AdminUserListData>> {
  return request(`${AUTH_BASE_URL}/api/admin/users/by-role/${role}`, {
    method: 'GET',
    skipErrorHandler: true,
  });
}

export async function batchLookupAdminUsers(
  ids: string[],
): Promise<AuthAPI.Response<{ users: AuthAPI.AdminUser[] }>> {
  return request(`${AUTH_BASE_URL}/api/admin/users/batch-lookup`, {
    method: 'POST',
    data: { ids },
    skipErrorHandler: true,
  });
}

export async function getAdminUserStats(): Promise<AuthAPI.Response<AuthAPI.AdminUserStats>> {
  return request(`${AUTH_BASE_URL}/api/admin/users/stats`, {
    method: 'GET',
    skipErrorHandler: true,
  });
}

export async function getAdminUserDetail(
  id: number,
): Promise<AuthAPI.Response<AuthAPI.AdminUserDetail>> {
  return request(`${AUTH_BASE_URL}/api/admin/users/${id}`, {
    method: 'GET',
    skipErrorHandler: true,
  });
}

export async function updateAdminUserRoles(
  id: number,
  params: AuthAPI.AdminUpdateRolesParams,
): Promise<AuthAPI.Response<AuthAPI.AdminUser>> {
  return request(`${AUTH_BASE_URL}/api/admin/users/${id}/roles`, {
    method: 'PUT',
    data: params,
    skipErrorHandler: true,
  });
}

export async function updateAdminUserStatus(
  id: number,
  params: AuthAPI.AdminUpdateStatusParams,
): Promise<AuthAPI.Response<AuthAPI.AdminUser>> {
  return request(`${AUTH_BASE_URL}/api/admin/users/${id}/status`, {
    method: 'PUT',
    data: params,
    skipErrorHandler: true,
  });
}

export async function getAdminUserLoginHistory(
  userId: number | string,
  params?: AuthAPI.GetLoginHistoryParams,
): Promise<AuthAPI.Response<AuthAPI.LoginHistoryData>> {
  return request(`${AUTH_BASE_URL}/api/admin/users/${userId}/login-history`, {
    method: 'GET',
    params,
    skipErrorHandler: true,
  });
}

export async function getAdminUserSessions(
  userId: number | string,
  params?: AuthAPI.GetSessionsParams,
): Promise<AuthAPI.Response<AuthAPI.SessionListData>> {
  return request(`${AUTH_BASE_URL}/api/admin/users/${userId}/sessions`, {
    method: 'GET',
    params,
    skipErrorHandler: true,
  });
}

export type ProviderType = 'image' | 'video' | 'audio' | 'llm';

export interface GenvasProvider {
  id?: string;
  name: string;
  displayName: string;

  types: ProviderType[];
  type?: ProviderType;
  adapterId: string;
  enabled: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface UpsertProviderRequest {
  name: string;
  displayName: string;

  types?: ProviderType[];
  type?: ProviderType;
  adapterId: string;
  enabled?: boolean;
  sortOrder?: number;
}

export interface PlatformProviderKeyView {
  provider: string;
  apiKey: string;
  hasKey: boolean;
  baseUrl: string;

  icon?: string;
  enabled: boolean;

  authType?: string;

  hasAuthExtras?: boolean;

  extraHeaders?: Record<string, string>;
  statusMap?: Record<string, string>;
  downloadAuthSame?: boolean;

  httpTimeoutSec?: number;

  modelsPath?: string;

  genericConfigTemplate?: Record<string, unknown>;
  maxConcurrent?: number;
  maxRpm?: number;
  leaseSeconds?: number;
  updatedBy?: string;
  updatedAt?: string;
}

export interface UpsertPlatformKeyRequest {

  apiKey?: string;
  baseUrl?: string;
  icon?: string;
  enabled?: boolean;

  authType?: string;

  authExtras?: Record<string, unknown> | null;

  extraHeaders?: Record<string, string> | null;

  statusMap?: Record<string, string> | null;

  downloadAuthSame?: boolean;

  httpTimeoutSec?: number;

  modelsPath?: string;

  genericConfigTemplate?: Record<string, unknown> | null;

  // 调用限流(provider 级):所有 caller 调本 provider 共用同一池排队。
  maxConcurrent?: number | null;
  maxRpm?: number | null;
  leaseSeconds?: number | null;
}

export async function listEnabledProviders(): Promise<AuthAPI.Response<{ providers: GenvasProvider[] }>> {
  return request(`${AUTH_BASE_URL}/api/auth/providers`, {
    method: 'GET',
    skipErrorHandler: true,
  });
}

export async function listAdminProviders(): Promise<AuthAPI.Response<{ providers: GenvasProvider[] }>> {
  return request(`${AUTH_BASE_URL}/api/admin/providers`, {
    method: 'GET',
    skipErrorHandler: true,
  });
}

export async function upsertAdminProvider(
  name: string,
  body: UpsertProviderRequest,
): Promise<AuthAPI.Response<GenvasProvider>> {
  return request(`${AUTH_BASE_URL}/api/admin/providers/${encodeURIComponent(name)}`, {
    method: 'PUT',
    data: body,
    skipErrorHandler: true,
  });
}

export async function deleteAdminProvider(name: string): Promise<AuthAPI.Response<null>> {
  return request(`${AUTH_BASE_URL}/api/admin/providers/${encodeURIComponent(name)}`, {
    method: 'DELETE',
    skipErrorHandler: true,
  });
}

export async function setAdminProviderEnabled(
  name: string,
  enabled: boolean,
): Promise<AuthAPI.Response<null>> {
  return request(`${AUTH_BASE_URL}/api/admin/providers/${encodeURIComponent(name)}/enabled`, {
    method: 'PATCH',
    data: { enabled },
    skipErrorHandler: true,
  });
}

export async function listAdminPlatformKeys(): Promise<AuthAPI.Response<{ keys: PlatformProviderKeyView[] }>> {
  return request(`${AUTH_BASE_URL}/api/admin/platform-keys`, {
    method: 'GET',
    skipErrorHandler: true,
  });
}

export async function getAdminPlatformKey(
  provider: string,
): Promise<AuthAPI.Response<PlatformProviderKeyView>> {
  return request(`${AUTH_BASE_URL}/api/admin/platform-keys/${encodeURIComponent(provider)}`, {
    method: 'GET',
    skipErrorHandler: true,
  });
}

export async function upsertAdminPlatformKey(
  provider: string,
  body: UpsertPlatformKeyRequest,
): Promise<AuthAPI.Response<PlatformProviderKeyView>> {
  return request(`${AUTH_BASE_URL}/api/admin/platform-keys/${encodeURIComponent(provider)}`, {
    method: 'PUT',
    data: body,
    skipErrorHandler: true,
  });
}

export async function deleteAdminPlatformKey(provider: string): Promise<AuthAPI.Response<null>> {
  return request(`${AUTH_BASE_URL}/api/admin/platform-keys/${encodeURIComponent(provider)}`, {
    method: 'DELETE',
    skipErrorHandler: true,
  });
}

export interface TestConnectivityCheck {
  name: string;
  ok: boolean;
  detail?: string;
}

export interface UpstreamModel {
  id: string;
  displayName?: string;
  ownedBy?: string;
  description?: string;
}

export interface TestConnectivityResult {
  ok: boolean;
  durationMs: number;
  endpoint?: string;
  statusCode?: number;
  responsePreview?: string;
  errorMessage?: string;
  checks: TestConnectivityCheck[];
  upstreamModels?: UpstreamModel[];
}

export async function testAdminPlatformKey(
  provider: string,

  body: {
    apiKey?: string;
    baseUrl?: string;
    modelsPath?: string;
    authType?: string;
  },
): Promise<AuthAPI.Response<TestConnectivityResult>> {
  return request(`${AUTH_BASE_URL}/api/admin/platform-keys/${encodeURIComponent(provider)}/test`, {
    method: 'POST',
    data: body,
    skipErrorHandler: true,
  });
}

export interface PlatformCreditGrantSettings {
  registrationCreditGrant: number;
  dailyCreditGrant: number;
  dailyGrantEnabled: boolean;
  updatedAt?: string;
  updatedBy?: string;
}

export async function adminGetCreditGrantSettings(): Promise<AuthAPI.Response<PlatformCreditGrantSettings>> {
  return request(`${AUTH_BASE_URL}/api/admin/platform-settings/credit-grant`, {
    method: 'GET',
    skipErrorHandler: true,
  });
}

export async function adminPutCreditGrantSettings(
  body: Partial<Pick<PlatformCreditGrantSettings, 'registrationCreditGrant' | 'dailyCreditGrant' | 'dailyGrantEnabled'>>,
): Promise<AuthAPI.Response<PlatformCreditGrantSettings>> {
  return request(`${AUTH_BASE_URL}/api/admin/platform-settings/credit-grant`, {
    method: 'PUT',
    data: body,
    skipErrorHandler: true,
  });
}

export interface UserCreditBalance {
  id?: string;
  userId: string;
  credits: number;
  totalGrant: number;
  totalSpend: number;
  updatedAt?: string;
  createdAt?: string;
}

export async function getMyCredits(): Promise<AuthAPI.Response<UserCreditBalance>> {
  return request(`${AUTH_BASE_URL}/api/auth/user/credits`, {
    method: 'GET',
    skipErrorHandler: true,
  });
}

export async function adminGetUserCredits(
  userId: number | string,
): Promise<AuthAPI.Response<{ credits: UserCreditBalance; user: any }>> {
  return request(`${AUTH_BASE_URL}/api/admin/users/${userId}/credits`, {
    method: 'GET',
    skipErrorHandler: true,
  });
}

export async function adminGrantUserCredits(
  userId: number | string,
  body: { amount: number; note?: string },
): Promise<AuthAPI.Response<UserCreditBalance>> {
  return request(`${AUTH_BASE_URL}/api/admin/users/${userId}/credits/grant`, {
    method: 'POST',
    data: body,
    skipErrorHandler: true,
  });
}

export interface CreditConfig {
  id: string;
  usdToCredits: number;
  minUsd: number;
  maxUsd: number;
  updatedAt?: string;
}

export async function adminGetCreditConfig(): Promise<
  AuthAPI.Response<CreditConfig>
> {
  return request(`${AUTH_BASE_URL}/api/admin/credit-config`, {
    method: 'GET',
    skipErrorHandler: true,
  });
}

export async function adminUpdateCreditConfig(body: {
  usdToCredits?: number;
  minUsd?: number;
  maxUsd?: number;
}): Promise<AuthAPI.Response<CreditConfig>> {
  return request(`${AUTH_BASE_URL}/api/admin/credit-config`, {
    method: 'PUT',
    data: body,
    skipErrorHandler: true,
  });
}

export interface CreditPack {
  id?: string;
  code: string;
  name: string;
  description?: string;
  priceUsd: number;
  credits: number;
  tagText?: string;
  limitPerUser: number;
  enabled: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface UpsertCreditPackRequest {
  code: string;
  name: string;
  description?: string;
  priceUsd?: number;
  credits?: number;
  tagText?: string;
  limitPerUser?: number;
  enabled?: boolean;
  sortOrder?: number;
}

export async function adminListCreditPacks(): Promise<
  AuthAPI.Response<{ packs: CreditPack[] }>
> {
  return request(`${AUTH_BASE_URL}/api/admin/credit-packs`, {
    method: 'GET',
    skipErrorHandler: true,
  });
}

export async function adminUpsertCreditPack(
  body: UpsertCreditPackRequest,
  isEdit = false,
): Promise<AuthAPI.Response<CreditPack>> {

  const url = isEdit
    ? `${AUTH_BASE_URL}/api/admin/credit-packs/${encodeURIComponent(body.code)}`
    : `${AUTH_BASE_URL}/api/admin/credit-packs`;
  return request(url, {
    method: isEdit ? 'PUT' : 'POST',
    data: body,
    skipErrorHandler: true,
  });
}

export async function adminDeleteCreditPack(
  code: string,
): Promise<AuthAPI.Response<null>> {
  return request(
    `${AUTH_BASE_URL}/api/admin/credit-packs/${encodeURIComponent(code)}`,
    { method: 'DELETE', skipErrorHandler: true },
  );
}

export interface CreditLedgerEntry {
  id?: string;
  userId: string;
  delta: number;
  balanceAfter: number;
  reason: string;
  refType?: string;
  refId?: string;
  caller?: string;
  createdAt: string;
}

export interface LedgerQuery {
  type?: 'spend' | 'grant' | '';
  caller?: string[];
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

export interface LedgerUserInfo {
  id: string;
  userId: number;
  nickname?: string;
  email?: string;
  avatar?: string;
}

export interface LedgerPage {
  entries: CreditLedgerEntry[];
  total: number;
  page: number;
  pageSize: number;
  users?: Record<string, LedgerUserInfo>;
}

function buildLedgerParams(q: LedgerQuery | undefined) {
  const params: Record<string, any> = {};
  if (q?.type) params.type = q.type;
  if (q?.caller?.length) params.caller = q.caller.join(',');
  if (q?.from) params.from = q.from;
  if (q?.to) params.to = q.to;
  if (q?.page && q.page > 0) params.page = q.page;
  if (q?.pageSize && q.pageSize > 0) params.pageSize = q.pageSize;
  return params;
}

export async function getMyCreditLedger(q?: LedgerQuery): Promise<AuthAPI.Response<LedgerPage>> {
  return request(`${AUTH_BASE_URL}/api/auth/user/credits/ledger`, {
    method: 'GET',
    params: buildLedgerParams(q),
    skipErrorHandler: true,
  });
}

export async function adminGetUserCreditLedger(
  userId: number | string,
  q?: LedgerQuery,
): Promise<AuthAPI.Response<LedgerPage>> {
  return request(`${AUTH_BASE_URL}/api/admin/users/${userId}/credits/ledger`, {
    method: 'GET',
    params: buildLedgerParams(q),
    skipErrorHandler: true,
  });
}

export async function adminListGlobalLedger(
  q?: LedgerQuery & { userId?: string },
): Promise<AuthAPI.Response<LedgerPage>> {
  const params = buildLedgerParams(q);
  if (q?.userId) params.userId = q.userId;
  return request(`${AUTH_BASE_URL}/api/admin/credits/ledger`, {
    method: 'GET',
    params,
    skipErrorHandler: true,
  });
}

export async function adminListTeams(
  params?: AdminAPI.ListTeamsParams,
): Promise<AuthAPI.Response<AdminAPI.ListTeamsData>> {
  return request(`${AUTH_BASE_URL}/api/admin/teams`, {
    method: 'GET',
    params,
    skipErrorHandler: true,
  });
}

export async function adminGetTeam(
  teamId: string,
): Promise<AuthAPI.Response<AdminAPI.Team>> {
  return request(
    `${AUTH_BASE_URL}/api/admin/teams/${encodeURIComponent(teamId)}`,
    { method: 'GET', skipErrorHandler: true },
  );
}

export async function adminListTeamMembers(
  teamId: string,
): Promise<AuthAPI.Response<{ members: AdminAPI.TeamMemberView[] }>> {
  return request(
    `${AUTH_BASE_URL}/api/admin/teams/${encodeURIComponent(teamId)}/members`,
    { method: 'GET', skipErrorHandler: true },
  );
}

export type SystemAnnouncementType = 'info' | 'warning' | 'release';

export interface SystemAnnouncement {
  id: string;
  type: SystemAnnouncementType;
  title: string;
  body?: string;
  version?: string;
  createdBy: string;
  createdAt: string;
}

export interface CreateAnnouncementBody {
  type: SystemAnnouncementType;
  title: string;
  body?: string;
  version?: string;
}

export interface ListAnnouncementsData {
  items: SystemAnnouncement[];
  total: number;
  page: number;
  pageSize: number;
}

export async function adminCreateAnnouncement(
  body: CreateAnnouncementBody,
): Promise<AuthAPI.Response<SystemAnnouncement>> {
  return request(`${AUTH_BASE_URL}/api/admin/announcements`, {
    method: 'POST',
    data: body,
    skipErrorHandler: true,
  });
}

export async function adminListAnnouncements(params?: {
  page?: number;
  pageSize?: number;
}): Promise<AuthAPI.Response<ListAnnouncementsData>> {
  return request(`${AUTH_BASE_URL}/api/admin/announcements`, {
    method: 'GET',
    params,
    skipErrorHandler: true,
  });
}
